import { Feature, FeatureCollection, Geometry, Point } from "geojson";
import React, { createContext, FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { ProviderLandscapeMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/ProviderLandscapeMapDataContext";
import MarketExpansionService from "@app/overviews/market-expansion-dashboard/marketExpansionService";
import { MarketExpansionCounty } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionCounty";
import {
  convertFootprintRecordToGeoJson,
  convertToFeatureCollection,
  getProviderCircleRadius,
} from "@app/overviews/market-expansion-dashboard/shared/marketExpansionMapHelper";
import { ProviderFootprintFeatureProperties } from "@app/overviews/market-expansion-dashboard/shared/ProviderFootprintFeatureProperties";
import { ProviderFootprintFilterCriteria } from "@app/overviews/market-expansion-dashboard/shared/ProviderFootprintFilterCriteria";
import { ProviderFootprintRecord } from "@app/overviews/market-expansion-dashboard/shared/ProviderFootprintRecord";
import { featureCollection } from "@turf/helpers";

interface ProviderFootprintMapDataContextProps {
  countyOutlineFeatures: FeatureCollection<Geometry, MarketExpansionCounty>;
  footprintGeoJson: FeatureCollection<Point, ProviderFootprintFeatureProperties>;
  providerFootprintData: Map<number, ProviderFootprintRecord[]> | undefined;
  selectedCEFootprintGeoJson: FeatureCollection<Point, ProviderFootprintFeatureProperties>;
  selectedContractEntity: number | null;
  setSelectedContractEntity: (groupingId: number | null) => void;
}

const initialValues: ProviderFootprintMapDataContextProps = {
  countyOutlineFeatures: featureCollection([]),
  footprintGeoJson: featureCollection([]),
  providerFootprintData: undefined,
  selectedCEFootprintGeoJson: featureCollection([]),
  selectedContractEntity: null,
  setSelectedContractEntity: () => undefined,
};

const ProviderFootprintMapDataContext = createContext(initialValues);

const ProviderFootprintMapDataContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const providerLandscapeContext = useContext(ProviderLandscapeMapDataContext);
  const filtersContext = useContext(MarketExpansionFiltersContext);
  const marketExpansionService = useRef(new MarketExpansionService()).current;

  const [providerFootprintData, setProviderFootprintData] = useState<Map<number, ProviderFootprintRecord[]> | undefined>(initialValues.providerFootprintData);
  const [selectedContractEntity, setSelectedContractEntity] = useState<number | null>(initialValues.selectedContractEntity);

  const countyOutlineFeatures = useMemo(() => {
    return convertToFeatureCollection(filtersContext.countyOptions);
  }, [filtersContext.countyOptions]);

  const providerFootprintFilterCriteria: ProviderFootprintFilterCriteria | undefined = useMemo(() => {
    if (providerLandscapeContext.countySummaryData === undefined) return undefined;
    const countySummaries = providerLandscapeContext.countySummaryData.filter((county) => filtersContext.selectedCounties?.includes(county.countySSA));
    const contractEntities = countySummaries.flatMap((county) => county.contractEntities);
    const groupingIds = contractEntities.map((ce) => ce.groupingId);
    const ccns = contractEntities.flatMap((ce) => ce.providers).map((p) => p.ccn);
    return { ccns, groupingIds };
  }, [providerLandscapeContext.countySummaryData, filtersContext.selectedCounties]);

  const fetchProviderFootprintRecords = (filterCriteria: ProviderFootprintFilterCriteria) => {
    setProviderFootprintData(undefined);
    marketExpansionService
      .searchProviderFootprintRecords(filterCriteria)
      .then((records) => {
        const recordsByGroupingId = new Map<number, ProviderFootprintRecord[]>();
        records.forEach((r) => {
          let providerList = recordsByGroupingId.get(r.groupingId);
          if (providerList === undefined) {
            providerList = [];
            recordsByGroupingId.set(r.groupingId, providerList);
          }
          providerList.push(r);
        });
        setProviderFootprintData(recordsByGroupingId);
      })
      .catch(() => setProviderFootprintData(new Map()));
  };

  useEffect(() => {
    if (providerFootprintFilterCriteria === undefined) {
      setProviderFootprintData(undefined);
    } else if (providerFootprintFilterCriteria.groupingIds.length && providerFootprintFilterCriteria.ccns.length) {
      fetchProviderFootprintRecords(providerFootprintFilterCriteria);
    } else {
      setProviderFootprintData(new Map());
    }
  }, [providerFootprintFilterCriteria]);

  const providerFeatures: Feature<Point, ProviderFootprintFeatureProperties>[] = useMemo(() => {
    if (providerFootprintData === undefined) return [];

    const cases = [...providerLandscapeContext.serviceAreaCasesByCCN.values()];
    const minCases = cases.length > 0 ? Math.min(...cases) : 0;
    const maxCases = cases.length > 0 ? Math.max(...cases) : 0;

    const records = [...providerFootprintData.values()].flat();
    const features = records.map((r) => {
      const serviceAreaCases = providerLandscapeContext.serviceAreaCasesByCCN.get(r.ccn) ?? 0;
      const circleRadius = getProviderCircleRadius(serviceAreaCases, minCases, maxCases);
      const colorKey = providerLandscapeContext.getContractEntityColorSlotKey(r.groupingId);
      return convertFootprintRecordToGeoJson(r, serviceAreaCases, circleRadius, colorKey);
    });

    return features.sort((a, b) => {
      const aCases = providerLandscapeContext.serviceAreaCasesByGroupingId.get(a.properties.groupingId) ?? 0;
      const bCases = providerLandscapeContext.serviceAreaCasesByGroupingId.get(b.properties.groupingId) ?? 0;
      return aCases - bCases;
    });
  }, [providerFootprintData, providerLandscapeContext.serviceAreaCasesByCCN, providerLandscapeContext.serviceAreaCasesByGroupingId, providerLandscapeContext.getContractEntityColorSlotKey]);

  const selectedCEFootprintGeoJson = useMemo(() => {
    const features = providerFeatures.filter((f) => f.properties.groupingId === selectedContractEntity);
    return featureCollection(features);
  }, [providerFeatures, selectedContractEntity]);

  const footprintGeoJson = useMemo(() => {
    const features = providerFeatures.filter((f) => f.properties.groupingId !== selectedContractEntity);
    return featureCollection(features);
  }, [providerFeatures, selectedContractEntity]);

  /** Deselect Contract Entity when Service Area changes */
  useEffect(() => {
    setSelectedContractEntity(null);
  }, [providerLandscapeContext.countySummaryData, filtersContext.selectedCounties]);

  const contextValue: ProviderFootprintMapDataContextProps = {
    countyOutlineFeatures,
    footprintGeoJson,
    providerFootprintData,
    selectedCEFootprintGeoJson,
    selectedContractEntity,
    setSelectedContractEntity,
  };

  return <ProviderFootprintMapDataContext.Provider value={contextValue}>{children}</ProviderFootprintMapDataContext.Provider>;
};

export { ProviderFootprintMapDataContext, ProviderFootprintMapDataContextProps, ProviderFootprintMapDataContextProvider };
